import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';

// ── WALLS THAT STOP YOU ──────────────────────────────────────────────────────
//
// The street used to be fly-with-a-clamp: eye height pinned, a rectangle at the
// city's edge, and nothing in between — you walked through every building,
// lamp post and parked car, which is the single fastest way for a world to stop
// feeling like a place (Brief_Collision_Physics_Architecture, slice 1).
//
// One BVH over the city's geometry, cut to the band a body occupies: triangles
// that reach between ankle and head height. Kerbs and ground pads sit below it,
// so you step up onto the pavement; awnings and balconies sit above it, so you
// walk under them. The body is a vertical capsule, and each frame it is pushed
// out of whatever it overlaps — the standard three-mesh-bvh character pattern,
// done in the ground plane only, because the street keeps you at eye height.
//
// Built once from the city's frozen originals (their world matrices are final),
// a few tens of milliseconds behind the loading screen. Other solid things —
// a sited venue's shell, a placed building — can be added with addCollider().

const BAND_LO = 0.12;   // ankle: below this is kerb and ground
const BAND_HI = 1.15;   // head: above this you pass underneath

export const collision = {
  bvh: null,
  mesh: null,
  version: 0,
  tris: 0,
};

function appendBand(positions, out) {
  // positions: Float32Array of world-space triangle soup (9 floats per tri)
  for (let i = 0; i < positions.length; i += 9) {
    const y0 = positions[i + 1], y1 = positions[i + 4], y2 = positions[i + 7];
    const lo = Math.min(y0, y1, y2); const hi = Math.max(y0, y1, y2);
    if (hi < BAND_LO || lo > BAND_HI) continue;
    // Near-flat, near-ground faces are floors and kerb tops, not walls.
    if (hi - lo < 0.02 && hi < 0.6) continue;
    for (let k = 0; k < 9; k++) out.push(positions[i + k]);
  }
}

function worldTriangles(mesh) {
  const g = mesh.geometry;
  const pos = g.getAttribute('position');
  if (!pos) return null;
  mesh.updateWorldMatrix(true, false);
  const m = mesh.matrixWorld;
  const idx = g.index;
  const n = idx ? idx.count : pos.count;
  const out = new Float32Array(n * 3);
  const v = new THREE.Vector3();
  for (let i = 0; i < n; i++) {
    v.fromBufferAttribute(pos, idx ? idx.getX(i) : i).applyMatrix4(m);
    out[i * 3] = v.x; out[i * 3 + 1] = v.y; out[i * 3 + 2] = v.z;
  }
  return out;
}

const extra = [];

/** Build the street's collision BVH from the city model. */
export function buildStreetCollision(model, { skip = () => false } = {}) {
  const t0 = performance.now();
  const soup = [];
  model.traverse((o) => {
    if (!o.isMesh || o.isBatchedMesh || o.isSkinnedMesh || o.userData?.cityBatch) return;
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    if (mats.some((m) => m?.name === 'RoadsGround') || skip(o)) return;
    const tris = worldTriangles(o);
    if (tris) appendBand(tris, soup);
  });
  for (const e of extra) appendBand(e, soup);
  rebuild(soup);
  console.log(`[collision] street BVH: ${collision.tris} wall triangles in the walk band, ${Math.round(performance.now() - t0)} ms`);
}

function rebuild(soup) {
  collision.bvh = null;
  if (!soup.length) { collision.tris = 0; collision.version += 1; return; }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(soup), 3));
  collision.bvh = new MeshBVH(g);
  collision.mesh = g;
  collision.tris = soup.length / 9;
  collision.version += 1;
}

/** Add a solid object (world space) to the street's colliders. */
export function addCollider(mesh) {
  const t = worldTriangles(mesh);
  if (t) extra.push(t);
}

export function clearStreetCollision() {
  collision.bvh = null; collision.mesh = null; collision.tris = 0; collision.version += 1;
  extra.length = 0;
}

// ── RESOLVE ──────────────────────────────────────────────────────────────────
const _seg = new THREE.Line3();
const _box = new THREE.Box3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();
const _push = new THREE.Vector3();

/**
 * Push a standing body out of the walls. `pos` is the body's ground position
 * (x, z used; y ignored) and is corrected in place. Returns true if it touched.
 */
// Moving or parked things that are not part of the city mesh (parked cars),
// as circles in the ground plane: { x, z, r, owner }. Written by whoever owns
// them; `skipOwner` lets a car ignore its own.
export const obstacles = [];

function resolveCircles(pos, radius, skipOwner) {
  let touched = false;
  for (let i = 0; i < obstacles.length; i++) {
    const o = obstacles[i];
    if (skipOwner != null && o.owner === skipOwner) continue;
    const dx = pos.x - o.x; const dz = pos.z - o.z;
    const d = Math.hypot(dx, dz); const min = radius + o.r;
    if (d >= min || d < 1e-5) continue;
    const k = (min - d) / d;
    pos.x += dx * k; pos.z += dz * k;
    touched = true;
  }
  return touched;
}

export function resolveBody(pos, radius = 0.24, skipOwner = null) {
  let touched = resolveCircles(pos, radius, skipOwner);
  const bvh = collision.bvh;
  if (!bvh) return touched;
  // A few relaxation passes settle corners, where two walls push at once.
  for (let pass = 0; pass < 3; pass++) {
    _seg.start.set(pos.x, BAND_LO + radius, pos.z);
    _seg.end.set(pos.x, BAND_HI - radius * 0.5, pos.z);
    _box.makeEmpty(); _box.expandByPoint(_seg.start); _box.expandByPoint(_seg.end);
    _box.min.addScalar(-radius); _box.max.addScalar(radius);
    let moved = false;
    bvh.shapecast({
      intersectsBounds: (box) => box.intersectsBox(_box),
      intersectsTriangle: (tri) => {
        const d = tri.closestPointToSegment(_seg, _a, _b);
        if (d < radius) {
          _push.subVectors(_b, _a); _push.y = 0;
          const len = _push.length();
          if (len < 1e-5) {
            // Dead centre on a face: push along its normal instead.
            tri.getNormal(_push); _push.y = 0;
            if (_push.lengthSq() < 1e-6) return false;
            _push.normalize();
          } else _push.multiplyScalar(1 / len);
          const depth = radius - d;
          pos.x += _push.x * depth; pos.z += _push.z * depth;
          _seg.start.x = _seg.end.x = pos.x; _seg.start.z = _seg.end.z = pos.z;
          moved = true; touched = true;
        }
        return false;
      },
    });
    if (!moved) break;
  }
  return touched;
}

/** Is there a wall between two ground points? (for the third-person camera) */
const _ray = new THREE.Ray();
export function raycastWalls(from, dir, far) {
  if (!collision.bvh) return null;
  _ray.origin.copy(from); _ray.direction.copy(dir);
  const hit = collision.bvh.raycastFirst(_ray, THREE.DoubleSide);
  return hit && hit.distance <= far ? hit : null;
}

/**
 * How far, and which way, a circle at (x, z) has to move to clear the walls —
 * one pass, written into `out` (x, z). Returns the penetration depth (0 = clear).
 * Cars use three of these along their length.
 */
const _c = new THREE.Vector3();
const _before = new THREE.Vector3();
export function pushOut(x, z, radius, out, skipOwner = null) {
  out.x = 0; out.z = 0;
  _c.set(x, 0, z);
  const before = _before.copy(_c);
  resolveBody(_c, radius, skipOwner);
  out.x = _c.x - before.x; out.z = _c.z - before.z;
  return Math.hypot(out.x, out.z);
}
